/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  AuthClientHeader,
  AuthClientHeader$inboundSchema,
} from "./authclientheader.js";
import {
  AuthClientServer,
  AuthClientServer$inboundSchema,
} from "./authclientserver.js";

/**
 * How the client authenticates upstream.
 */
export const AuthClientClientType = {
  RemoteSession: "remote_session",
  ServiceAccount: "service_account",
  StaticCredential: "static_credential",
  PassThrough: "pass_through",
  Ema: "ema",
} as const;
/**
 * How the client authenticates upstream.
 */
export type AuthClientClientType = OpenEnum<typeof AuthClientClientType>;

export const AuthClientSource = {
  Backfill: "backfill",
  Admin: "admin",
} as const;
export type AuthClientSource = OpenEnum<typeof AuthClientSource>;

/**
 * A configured way for Speakeasy to authenticate to an upstream MCP server. Who may use it is controlled by mcp:auth grants on the client, never by who configured it. Auth clients are typed auth configuration: they take effect only where typed auth client resolution is enforced, which is being rolled out in stages. Elsewhere, including in shadow mode, an MCP server's existing upstream configuration keeps serving requests.
 */
export type AuthClient = {
  /**
   * How the client authenticates upstream.
   */
  clientType: AuthClientClientType;
  /**
   * When the client was created.
   */
  createdAt: Date;
  /**
   * Whether the client is enabled. Where typed auth client resolution is enforced, a disabled client is never selected or resolved. Disabling revokes no upstream credentials, and elsewhere, including in shadow mode, an MCP server's existing upstream configuration keeps serving.
   */
  enabled: boolean;
  /**
   * The headers the client sends upstream.
   */
  headers: Array<AuthClientHeader>;
  /**
   * The auth client id.
   */
  id: string;
  /**
   * The client's name.
   */
  name: string;
  /**
   * Whether each caller uses their own connection through the client. A grant on a personal client never lets one user use another user's connection.
   */
  personal: boolean;
  /**
   * The project the client belongs to. Omitted for a client shared across the organization's projects.
   */
  projectId?: string | undefined;
  /**
   * The OAuth registration behind remote_session, service_account and ema clients.
   */
  remoteSessionClientId?: string | undefined;
  /**
   * The MCP servers that offer the client. Where typed auth client resolution is enforced, changing or disabling the client changes upstream behavior on every one of them.
   */
  servers: Array<AuthClientServer>;
  authClientSource: AuthClientSource;
  /**
   * When the client was last updated.
   */
  updatedAt: Date;
};

/** @internal */
export const AuthClientClientType$inboundSchema: z.ZodMiniType<
  AuthClientClientType,
  unknown
> = openEnums.inboundSchema(AuthClientClientType);

/** @internal */
export const AuthClientSource$inboundSchema: z.ZodMiniType<
  AuthClientSource,
  unknown
> = openEnums.inboundSchema(AuthClientSource);

/** @internal */
export const AuthClient$inboundSchema: z.ZodMiniType<AuthClient, unknown> = z
  .pipe(
    z.object({
      client_type: AuthClientClientType$inboundSchema,
      created_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      enabled: z.boolean(),
      headers: z.array(AuthClientHeader$inboundSchema),
      id: z.string(),
      name: z.string(),
      personal: z.boolean(),
      project_id: z.optional(z.string()),
      remote_session_client_id: z.optional(z.string()),
      servers: z.array(AuthClientServer$inboundSchema),
      source: AuthClientSource$inboundSchema,
      updated_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
    }),
    z.transform((v) => {
      return remap$(v, {
        "client_type": "clientType",
        "created_at": "createdAt",
        "project_id": "projectId",
        "remote_session_client_id": "remoteSessionClientId",
        "source": "authClientSource",
        "updated_at": "updatedAt",
      });
    }),
  );

export function authClientFromJSON(
  jsonString: string,
): SafeParseResult<AuthClient, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AuthClient$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AuthClient' from JSON`,
  );
}
